/* eslint-disable @typescript-eslint/no-confusing-void-expression */

/* eslint-disable import/no-internal-modules */

/* eslint-disable sonarjs/no-duplicate-string */

/* eslint-disable @typescript-eslint/no-use-before-define */
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { useAnalytics } from '#~/analytics/index.js';
import { toIdFromHex } from '#~/api/utils.js';
import { useAuth0 } from '#~/auth/use-auth0.js';
import { nats } from '#~/nats/index.js';
import { Button } from '@studio/design-system/button';
import * as Dialog from '@studio/design-system/dialog';
import { DataSTag, Sel } from '#~/stag/index.js';
import { Icon } from '@studio/design-system/icon';
import * as Input from '@studio/design-system/input';
import { Spinner } from '@studio/design-system/spinner';
import {
  BillingRegistryGetPlansForRoomRequest,
  BillingRegistryGetPlansForRoomResponse,
  BillingRegistryPlan,
  BillingRegistryPlanFrequency,
  BillingRegistryPlanPurchase,
  BillingRegistryPlanPurchaseState as State
} from '@tryiris-ai/common.protojs/api/v1/billing_registry';
import { BillingRegistryGetBillingPlansForRoom } from '@tryiris-ai/mailroom-helper/billing';
import { clsx } from 'cn';
import type { ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { Tabs } from './components/tabs.jsx';
import type { BillingInfo } from './types.js';
import {
  FuturePlanPurchaseQuote,
  futurePurchaseAmount,
  futurePurchaseDate
} from './future-plan-purchase-summary.jsx';

export type PurchaseQuote =
  | {
      kind: 'immediate';
      priceId: string;
      amount: number;
      currency: string;
    }
  | { kind: 'future'; purchase: BillingRegistryPlanPurchase };
export interface TrialQuoteSelection {
  plan: 'standard' | 'pro';
  type: 'monthly' | 'yearly';
  priceId: string;
  trialEnd: number;
  promotionCode: string;
}
export interface PlanSelection {
  plan: 'free' | 'pro' | 'standard' | 'enterprise';
  type: 'monthly' | 'yearly';
  purchaseConfirmed?: boolean;
  quote?: PurchaseQuote;
}
interface Props {
  billing: BillingInfo;
  selectedRoomID: string;
  onClose?: () => void;
  onSelect: (options: PlanSelection) => Promise<void>;
  onQuote?: (options: TrialQuoteSelection) => Promise<BillingRegistryPlanPurchase>;
  onCancelQuote?: () => void;
  ownerKey?: string | undefined;
  quoteRevision?: number;
  canSchedule?: boolean;
}

export const ChooseYourPlans = ({
  selectedRoomID,
  billing,
  onClose,
  onSelect,
  onQuote,
  onCancelQuote,
  ownerKey,
  quoteRevision,
  canSchedule = true
}: Props) => {
  const [planInfo, setPlanInfo] = useState<BillingRegistryGetPlansForRoomResponse>();

  const [selectedTab, setSelectedTab] = useState<'monthly' | 'yearly'>(
    billing.activePlan.type === 'yearly' ? 'yearly' : 'monthly'
  );

  const currentPlan = billing.activePlan.planSystem.toLocaleLowerCase();

  const { getAccessTokenSilently } = useAuth0();
  const { track } = useAnalytics();

  const [isCTALoading, setIsCTALoading] = useState(false);
  const submitting = useRef(false);
  const preparingQuote = useRef(false);
  const [confirmation, setConfirmation] = useState<PlanSelection>();
  const [quoteError, setQuoteError] = useState('');
  const [promotionCode, setPromotionCode] = useState('');
  const [catalogRevision, setCatalogRevision] = useState(0);
  const promotionId = useId();
  const quoteGeneration = useRef(0);
  const trialEnd = billing.activePlan.trialEndsAt?.getTime();
  // The panel can predate a cancellation or payment. Use the fresh catalog's
  // Studio decision; older servers retain the trial/Free consent fallback.
  const needsPurchase =
    planInfo?.purchaseRequired ??
    (billing.activePlan.trialEndsAt !== undefined || currentPlan === 'free');

  // Editing financial choices invalidates both the displayed price and its parent's consent identity.
  const invalidateQuote = () => {
    quoteGeneration.current++;
    onCancelQuote?.();
    setConfirmation(undefined);
    setQuoteError('');
    // Per rule: preserve-mutation-state-when-invalidating-read (ai.knowledge/global).
    // A code or period edit can replace a quote read, but cannot finish a plan change.
    if (preparingQuote.current) {
      preparingQuote.current = false;
      submitting.current = false;
      setIsCTALoading(false);
    }
  };

  const submitSelection = async (options: PlanSelection) => {
    // React state updates are asynchronous; the synchronous guard prevents
    // repeated submissions before the loading button can render.
    if (submitting.current === true) return;
    submitting.current = true;
    setIsCTALoading(true);
    try {
      // Track the appropriate event based on the plan and billing frequency
      if (options.plan === 'free' && currentPlan !== 'free') {
        track(EVENT_NAMES.DOWNGRADE_PLAN, EVENT_CATEGORIES.BILLING, {
          planType: options.plan,
          billingFrequency: options.type
        });
      } else if (options.plan === 'standard') {
        if (options.type === 'yearly') {
          track(EVENT_NAMES.SELECT_ANNUAL_STANDARD_PLAN, EVENT_CATEGORIES.BILLING, {
            planType: options.plan,
            billingFrequency: options.type
          });
        } else {
          track(EVENT_NAMES.SELECT_STANDARD_PLAN, EVENT_CATEGORIES.BILLING, {
            planType: options.plan,
            billingFrequency: options.type
          });
        }
      } else if (options.plan === 'pro') {
        track(EVENT_NAMES.SELECT_PRO_PLAN, EVENT_CATEGORIES.BILLING, {
          planType: options.plan,
          billingFrequency: options.type
        });
      } else if (options.plan === 'enterprise') {
        track(EVENT_NAMES.CLICK_UPGRADE_PLAN, EVENT_CATEGORIES.BILLING, {
          planType: options.plan,
          billingFrequency: options.type
        });
      }

      await onSelect(options);
    } catch (error) {
      console.error(error);
    } finally {
      submitting.current = false;
      setIsCTALoading(false);
    }
  };

  const handleSelect = async (options: PlanSelection) => {
    if (submitting.current === true) return;
    if (needsPurchase && (options.plan === 'standard' || options.plan === 'pro')) {
      const offered = options.plan === 'standard' ? planInfo?.standardPlan : planInfo?.proPlan;
      const priceId = options.type === 'monthly' ? offered?.priceMonthlyId : offered?.priceYearlyId;
      if (
        offered === undefined ||
        priceId === undefined ||
        priceId === '' ||
        offered.currency === ''
      ) {
        setQuoteError(
          'The current price is unavailable. Close and reopen the plan chooser to refresh it.'
        );
        return;
      }
      // Same-tier Standard preserves its trial; the configured higher tier requires payment now.
      if (trialEnd !== undefined && options.plan === 'standard') {
        const generation = ++quoteGeneration.current;
        preparingQuote.current = true;
        submitting.current = true;
        setIsCTALoading(true);
        setQuoteError('');
        try {
          if (onQuote === undefined || !canSchedule || !Number.isFinite(trialEnd))
            throw new Error('trial quote unavailable');
          const request: TrialQuoteSelection = {
            plan: options.plan,
            type: options.type,
            priceId,
            trialEnd: trialEnd / 1000,
            promotionCode: promotionCode.trim()
          };
          const quoted = await onQuote(request);
          if (generation !== quoteGeneration.current) return;
          // Consent uses one exact server quote, never a local estimate or an existing Checkout.
          if (
            quoted.state !== State.PURCHASE_STATE_QUOTED ||
            typeof quoted.quoteId !== 'string' ||
            quoted.quoteId === '' ||
            quoted.priceId !== priceId ||
            quoted.plan !== BillingRegistryPlan.PLAN_STANDARD ||
            quoted.frequency !==
              (options.type === 'yearly'
                ? BillingRegistryPlanFrequency.PLAN_FREQUENCY_YEARLY
                : BillingRegistryPlanFrequency.PLAN_FREQUENCY_MONTHLY) ||
            quoted.effectiveAt !== request.trialEnd ||
            futurePurchaseDate(quoted.effectiveAt) === undefined ||
            quoted.firstRenewalAt <= quoted.effectiveAt ||
            quoted.recoveryUntil <= quoted.effectiveAt ||
            futurePurchaseAmount(quoted.firstInvoiceAmount, quoted.currency) === undefined ||
            futurePurchaseAmount(quoted.recurringInvoiceAmount, quoted.currency) === undefined ||
            quoted.checkoutSessionId !== '' ||
            quoted.subscriptionId !== '' ||
            quoted.checkoutUrl !== '' ||
            quoted.paymentUrl !== ''
          )
            throw new Error('trial quote mismatch');
          // A shared response cannot change the price after the owner reviews it.
          setConfirmation({
            ...options,
            quote: { kind: 'future', purchase: Object.freeze({ ...quoted }) }
          });
        } catch {
          if (generation === quoteGeneration.current)
            setQuoteError(
              'We could not prepare a current quote. Check the promotion code or refresh the plans and try again.'
            );
        } finally {
          if (generation === quoteGeneration.current) {
            preparingQuote.current = false;
            submitting.current = false;
            setIsCTALoading(false);
          }
        }
        return;
      }
      setConfirmation({
        ...options,
        quote: {
          kind: 'immediate',
          priceId,
          amount: options.type === 'monthly' ? offered.priceMonthly : offered.priceYearly,
          currency: offered.currency
        }
      });
      return;
    }
    await submitSelection(options);
  };

  useEffect(() => {
    let canceled = false;
    quoteGeneration.current++;
    preparingQuote.current = false;
    submitting.current = false;
    setIsCTALoading(false);
    setPlanInfo(undefined);
    setConfirmation(undefined);
    setQuoteError('');
    const fn = async () => {
      // ENG-4201: once the mailroom-helper bump lands, BillingRegistryGetBillingPlansForRoom
      // constructs with staleOnError:false, so a failed billing read REJECTS instead of
      // resolving with a body whose `.error` is set. This effect runs as `void fn()` with no
      // caller to catch, so an uncaught rejection here becomes an UNHANDLED promise rejection
      // during a backend outage. Contain it: log and bail, leaving planInfo unset (the UI
      // already renders the loading/empty state) rather than crashing or showing a stale plan.
      // Mirrors the existing limits.tsx / select-studio.tsx try/catch pattern.
      // Per rule: reference-rules-in-comments (ai.knowledge/global).
      try {
        const req = BillingRegistryGetPlansForRoomRequest.fromPartial({
          roomID: toIdFromHex(selectedRoomID)!,
          token: await getAccessTokenSilently()
        });

        if (canceled) return;
        const resp = await BillingRegistryGetBillingPlansForRoom.request(
          nats.cloudConnection(),
          req,
          { force: true, cacheTTL: 0, staleOnError: false }
        );

        if (resp.error) {
          console.error('Error:', resp.error);
          return;
        }

        if (!canceled) setPlanInfo(resp);
      } catch (error) {
        console.error('Failed to load billing plans for room:', error);
      }
    };

    void fn();
    return () => {
      canceled = true;
      quoteGeneration.current++;
      preparingQuote.current = false;
      onCancelQuote?.();
    };
  }, [
    selectedRoomID,
    setPlanInfo,
    trialEnd,
    ownerKey,
    quoteRevision,
    canSchedule,
    catalogRevision
  ]);

  // A code entered for another owner, Studio or trial must not carry into a new purchase context.
  useEffect(() => setPromotionCode(''), [selectedRoomID, ownerKey, trialEnd, canSchedule]);

  if (trialEnd !== undefined && !canSchedule)
    return (
      <div className='mx-24 rounded-8 bg-base-100 p-32'>
        <Dialog.Title>Studio plans</Dialog.Title>
        <p className='my-24'>Only the Studio owner can schedule a paid plan for this trial.</p>
        <Button onClick={onClose} {...DataSTag(Sel.AccountSettings, 'plan-close')}>
          Close
        </Button>
      </div>
    );

  if (confirmation?.quote !== undefined) {
    const { quote } = confirmation;
    const label = confirmation.plan === 'standard' ? 'Standard' : 'Pro';
    const period = confirmation.type === 'monthly' ? 'month' : 'year';
    const future = quote.kind === 'future' ? quote.purchase : undefined;
    return (
      <div
        className='relative mx-24 max-h-[calc(100vh_-_40px)] overflow-auto rounded-8 bg-base-100 p-32'
        {...DataSTag(Sel.AccountSettings, 'purchase-confirmation')}
      >
        <Dialog.Title>
          {future === undefined ? `Confirm ${label} purchase` : `Schedule ${label}`}
        </Dialog.Title>
        <div className='mt-24 break-words'>{billing.studio.name}</div>
        <div className='mt-16'>
          Current plan:{' '}
          {billing.activePlan.trialEndsAt !== undefined
            ? 'Standard trial'
            : billing.activePlan.planHuman}
        </div>
        {future !== undefined ? (
          <div className='mt-16 flex flex-col gap-12'>
            <p>Standard trial continues until {futurePurchaseDate(future.effectiveAt)}.</p>
            <FuturePlanPurchaseQuote receipt={future} />
            <p>
              No subscription fee is charged during the remaining trial. The first invoice is due at{' '}
              {futurePurchaseDate(future.effectiveAt)}.
            </p>
            <p>
              {label} starts after the trial once the first invoice is confirmed. Secure card setup
              confirms this future purchase and does not change the saved cloud card.
            </p>
          </div>
        ) : (
          quote.kind === 'immediate' && (
            <>
              <div className='mt-16 font-demibold'>
                {label}:{' '}
                {new Intl.NumberFormat(undefined, {
                  style: 'currency',
                  currency: quote.currency
                }).format(quote.amount)}{' '}
                per {period}, renewing every {period}.
              </div>
              <p>Paid access starts immediately after confirmed payment.</p>
              {trialEnd !== undefined && (
                <p>Confirmed payment replaces your remaining Standard trial with {label}.</p>
              )}
              <p>
                Checkout collects your payment method and asks you to confirm this recurring
                purchase, including when a cloud card is already saved. Tax and any applied
                discounts are shown in Checkout.
              </p>
              <p>
                Your current access continues until payment is confirmed. Saving a cloud card alone
                does not buy this plan.
              </p>
              <p>
                Included cloud usage comes with this plan. Extra cloud usage requires a separately
                saved cloud card in Billing. This purchase does not change that card.
              </p>
            </>
          )
        )}
        <div className='mt-24 flex flex-wrap gap-12'>
          <Button
            {...DataSTag(Sel.AccountSettings, 'purchase-confirm')}
            className='focus-visible:ring-primary/50 focus-visible:ring-2'
            disabled={isCTALoading}
            onClick={() => submitSelection({ ...confirmation, purchaseConfirmed: true })}
          >
            {future === undefined ? 'Continue to secure checkout' : `Schedule ${label}`}
          </Button>
          <Button
            {...DataSTag(Sel.AccountSettings, 'purchase-cancel')}
            className='focus-visible:ring-primary/50 focus-visible:ring-2'
            disabled={isCTALoading}
            variant='secondary'
            onClick={() => {
              invalidateQuote();
            }}
          >
            {future === undefined ? 'Cancel' : 'Edit selection'}
          </Button>
        </div>
      </div>
    );
  }

  // Keep the close control outside the scroller so keyboard wrap cannot hide it.
  return (
    <div className='relative mx-16 w-auto rounded-8 bg-base-100 min-[720px]:mx-64'>
      <Button
        className='focus-visible:ring-primary/50 absolute right-10 top-10 z-10 focus-visible:ring-2'
        aria-label='Close plan chooser'
        variant='secondary'
        onClick={onClose}
        {...DataSTag(Sel.AccountSettings, 'plan-close')}
      >
        <Icon icon='close' />
      </Button>
      <div
        {...DataSTag(Sel.AccountSettings, 'plan-chooser-content')}
        className='max-h-[calc(100vh_-_60px)] overflow-auto rounded-8 px-16 py-40 min-[720px]:px-64'
      >
        <div className='text-center'>
          <Dialog.Title>Choose Your Studio Plan</Dialog.Title>
          <div className='mt-24 text-primary'>{billing.studio.name}</div>
        </div>
        {quoteError !== '' && (
          <div className='my-16 flex flex-wrap items-center gap-12'>
            <p role='alert'>{quoteError}</p>
            <Button
              variant='secondary'
              className='focus-visible:ring-primary/50 focus-visible:ring-2'
              {...DataSTag(Sel.AccountSettings, 'refresh-plans')}
              onClick={() => {
                invalidateQuote();
                setCatalogRevision((value) => value + 1);
              }}
            >
              Refresh plans
            </Button>
          </div>
        )}
        {/* Header */}
        <div className='my-28 flex justify-center gap-x-8'>
          <Tabs
            selectedTab={selectedTab}
            setSelectedTab={(tab) => {
              if (tab !== selectedTab) {
                invalidateQuote();
                setSelectedTab(tab);
              }
            }}
          />
        </div>

        {trialEnd !== undefined && (
          <Input.Container
            htmlFor={promotionId}
            label='Promotion code (optional)'
            className='mx-auto mb-24 max-w-[384px]'
          >
            <Input.Root
              id={promotionId}
              value={promotionCode}
              aria-describedby={`${promotionId}-help`}
              autoCapitalize='none'
              spellCheck={false}
              className='focus-visible:ring-primary/50 bg-base-200 focus-visible:ring-2'
              {...DataSTag(Sel.AccountSettings, 'promotion-code')}
              onChange={(value) => {
                invalidateQuote();
                setPromotionCode(value);
              }}
            />
            <p id={`${promotionId}-help`} className='text-sm text-muted'>
              Standard discounts appear in the next quote. For Pro, enter your code in secure
              checkout.
            </p>
          </Input.Container>
        )}

        {/* Plans */}
        <div className='my-4 flex flex-wrap justify-center gap-24'>
          {!planInfo && (
            <div className='h-96 flex w-full items-center justify-center'>
              <Card features={[]} prepend={<Spinner className='mx-auto' />}></Card>
            </div>
          )}

          {planInfo && (
            <>
              {/* Basic Plan */}
              <Card
                features={planInfo?.freePlan?.features ?? []}
                note='*included usage resets monthly'
              >
                <PlanDetails
                  loading={isCTALoading}
                  activePlan={currentPlan === 'free' && selectedTab === billing.activePlan.type}
                  cta={currentPlan === 'free' ? 'Current Plan' : 'Downgrade Plan'}
                  tag='plan-free'
                  onCta={() => handleSelect({ plan: 'free', type: selectedTab })}
                  price={{
                    name: planInfo?.freePlan?.title ?? ''
                  }}
                  sources={<div>{planInfo?.freePlan?.subline}</div>}
                  type={planInfo?.freePlan?.header}
                />
              </Card>

              {/* Standard Plan */}
              <Card
                features={planInfo?.standardPlan?.features ?? []}
                note={`*Overages billed at ${planInfo?.standardPlan?.currencyUnitPrefix}${planInfo?.standardPlan?.limits?.cloudUsageOverageRate?.toFixed(2)}${planInfo?.standardPlan?.currencyUnitSuffix} per GB`}
                prepend={
                  <div className='mb-8'>
                    Everything in <strong>Free</strong> plus...
                  </div>
                }
              >
                <PlanDetails
                  loading={isCTALoading}
                  activePlan={
                    currentPlan === 'standard' &&
                    selectedTab === billing.activePlan.type &&
                    !needsPurchase
                  }
                  cta={
                    needsPurchase
                      ? 'Buy Standard'
                      : currentPlan === 'standard' && selectedTab === billing.activePlan.type
                        ? 'Current Plan'
                        : currentPlan === 'pro'
                          ? 'Downgrade Plan'
                          : 'Upgrade Plan'
                  }
                  tag='plan-standard'
                  onCta={() => handleSelect({ plan: 'standard', type: selectedTab })}
                  price={{
                    name:
                      selectedTab === 'monthly'
                        ? `${planInfo?.standardPlan?.currencyUnitPrefix}${planInfo?.standardPlan?.priceMonthly.toFixed(2)}${planInfo?.standardPlan?.currencyUnitSuffix}`
                        : `${planInfo?.standardPlan?.currencyUnitPrefix}${planInfo?.standardPlan?.priceYearly.toFixed(2)}${planInfo?.standardPlan?.currencyUnitSuffix}`,

                    type: selectedTab === 'monthly' ? 'per month' : 'per year'
                  }}
                  sources={
                    <div>
                      Add up to{' '}
                      <span className='text-primary'>
                        {planInfo?.standardPlan?.limits?.maxSources} video sources
                      </span>
                    </div>
                  }
                  type={planInfo?.standardPlan?.header}
                />
              </Card>

              {/* Pro Plan */}
              <Card
                features={planInfo?.proPlan?.features ?? []}
                note={`*Overages billed at ${planInfo?.proPlan?.currencyUnitPrefix}${planInfo?.proPlan?.limits?.cloudUsageOverageRate?.toFixed(2)}${planInfo?.proPlan?.currencyUnitSuffix} per GB`}
                prepend={
                  <div className='mb-8'>
                    Everything in <strong>Standard</strong> plus...
                  </div>
                }
              >
                <PlanDetails
                  loading={isCTALoading}
                  activePlan={
                    currentPlan === 'pro' &&
                    selectedTab === billing.activePlan.type &&
                    !needsPurchase
                  }
                  cta={
                    needsPurchase
                      ? 'Buy Pro'
                      : currentPlan === 'pro' && selectedTab === billing.activePlan.type
                        ? 'Current Plan'
                        : 'Upgrade Plan'
                  }
                  tag='plan-pro'
                  onCta={() => handleSelect({ plan: 'pro', type: selectedTab })}
                  price={{
                    name:
                      selectedTab === 'monthly'
                        ? `${planInfo?.proPlan?.currencyUnitPrefix}${planInfo?.proPlan?.priceMonthly.toFixed(2)}${planInfo?.proPlan?.currencyUnitSuffix}`
                        : `${planInfo?.proPlan?.currencyUnitPrefix}${planInfo?.proPlan?.priceYearly.toFixed(2)}${planInfo?.proPlan?.currencyUnitSuffix}`,
                    type: selectedTab === 'monthly' ? 'per month' : 'per year'
                  }}
                  sources={
                    <div>
                      Add up to{' '}
                      <span className='text-primary'>
                        {planInfo?.proPlan?.limits?.maxSources} video sources
                      </span>
                    </div>
                  }
                  type={planInfo?.proPlan?.header}
                />
              </Card>

              {/* Enterprise Plan */}
              <Card
                features={[]}
                note={''}
                prepend={
                  <div className='mb-8'>
                    You will be contacted by a member of our team to discuss your needs.
                  </div>
                }
              >
                <PlanDetails
                  loading={isCTALoading}
                  activePlan={
                    currentPlan === 'enterprise' && selectedTab === billing.activePlan.type
                  }
                  cta={currentPlan === 'enterprise' ? 'Current Plan' : 'Contact Sales'}
                  tag='plan-enterprise'
                  onCta={() => handleSelect({ plan: 'enterprise', type: selectedTab })}
                  price={{
                    name: 'Custom Pricing',
                    type: ''
                  }}
                  sources={<div className='h-[15px]'></div>}
                  type={'Enterprise Studio'}
                />
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PlanDetails = ({
  activePlan,
  cta,
  onCta,
  price,
  sources,
  type,
  loading,
  tag
}: {
  activePlan: boolean;
  cta: string;
  onCta?: () => void;
  price: { name: string; type?: string };
  sources: ReactNode;
  type: ReactNode;
  loading: boolean;
  tag: string;
}) => (
  <div className='px-24'>
    <div className='text-primary'>{type}</div>
    <div className='mt-12'>
      <span className='text-2xl font-demibold'>{price.name}</span>
      <span className='pl-8 text-muted'>{price.type}</span>
    </div>
    <div className='mt-6 text-sm'>{sources}</div>
    <div className='mt-18'>
      <Button
        {...DataSTag(Sel.AccountSettings, tag)}
        className={clsx('block h-auto w-full py-16 text-md', {
          'bg-base-50 text-secondary !opacity-100': activePlan
        })}
        disabled={activePlan || loading}
        onClick={onCta}
        size='lg'
      >
        {cta}
      </Button>
    </div>
  </div>
);

const Card = ({
  children,
  features,
  note,
  prepend
}: {
  children?: ReactNode;
  features: string[];
  note?: React.ReactNode;
  prepend?: ReactNode;
}) => (
  <div className='min-w-[min(320px,100%)] max-w-[384px] flex-1 rounded-12 bg-base-200 py-20 pb-40'>
    {children}

    <div className='my-32 border-t-1 border-t-base-100' />

    <div className='mt-32 flex flex-col gap-y-12 px-24'>
      {prepend}
      {features.map((feature) => (
        <div className='flex items-center gap-8 leading-150' key={feature}>
          <i className='i-mdi-check-circle-outline text-xl text-primary' />
          <span className='text-muted'>{feature}</span>
        </div>
      ))}
      <div className='mt-8 text-sm italic text-muted'>{note}</div>
    </div>
  </div>
);
